import { isValid, startOfDay } from "date-fns";
import { useTranslation } from "react-i18next";
import { Notice } from "~/components/notice";
import { useSalesData } from "~/contexts/sales-data-context";

// Title-level banner when either channel has partial history; stays in lockstep with per-chart pills.
const shouldShowPartialHistoryBanner = ({
  publishedAt,
  firstMarketSaleDate,
  firstAmazonSaleDate,
  selectedStart,
  selectedEnd,
}: {
  publishedAt?: Date | null;
  firstMarketSaleDate?: Date;
  firstAmazonSaleDate?: Date;
  selectedStart?: Date;
  selectedEnd?: Date;
}): boolean => {
  if (!selectedStart || !selectedEnd) {
    return false;
  }

  const start = startOfDay(selectedStart);
  const end = startOfDay(selectedEnd);
  const published =
    publishedAt && isValid(publishedAt) ? startOfDay(publishedAt) : undefined;

  return [firstMarketSaleDate, firstAmazonSaleDate].some((firstDataDate) => {
    if (!firstDataDate) {
      return false;
    }
    const earliest = startOfDay(firstDataDate);

    const truncationBound = published ?? start;
    if (earliest >= start && earliest <= end && earliest > truncationBound) {
      return true;
    }

    // Selected window ends before first data but after publication.
    return published !== undefined && end >= published && end < earliest;
  });
};

export const SalesHistoryBanner = () => {
  const { t } = useTranslation(["productDetails"]);
  const { firstMarketSaleDate, firstAmazonSaleDate, publishedAt, from, to } =
    useSalesData();

  const showBanner = shouldShowPartialHistoryBanner({
    publishedAt,
    firstMarketSaleDate,
    firstAmazonSaleDate,
    selectedStart: from,
    selectedEnd: to,
  });

  if (!showBanner) {
    return null;
  }

  return (
    <Notice
      variant="caution"
      title={t("salesData.banner.partialHistory.title")}
      data-testid="sales-history-banner"
    >
      {t("salesData.banner.partialHistory.body")}
    </Notice>
  );
};

export { shouldShowPartialHistoryBanner };
